import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

const TableWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 10px 0 6px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
  background: #fff;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  background-color: #fff;
  font-size: 0.7rem;

  th,
  td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 12px 8px;
    line-height: 2.1;
  }

  th {
    background: #9848c7; /* zakupy kolor */
    color: #fff;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
  tr:hover {
    background-color: #eef6ff;
  }

  /* top 3 most negative rows */
  .highlight-low {
    background-color: rgba(255, 0, 0, 0.1);
    font-weight: bold;
  }
`;

const monthNames = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

const Top10NegativePurchaseTransactions = ({ zakupyData = [] }) => {
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [rows, setRows] = useState([]);

  const availableYears = useMemo(() => {
    const s = new Set();
    zakupyData.forEach((r) => {
      const y = parseInt(r.Rok, 10);
      if (!isNaN(y)) s.add(y);
    });
    return [...s].sort((a, b) => b - a);
  }, [zakupyData]);

  useEffect(() => {
    if (!selectedYear && availableYears.length) {
      setSelectedYear(String(availableYears[0]));
    }
  }, [availableYears, selectedYear]);

  // K_42 + K_43 – ujemne traktujemy jako korekty
  const calculateTransactionValue = (row) => {
    const k42 = parseFloat(row.K_42) || 0;
    const k43 = parseFloat(row.K_43) || 0;
    return k42 + k43;
  };

  const fmtDate = (val) => {
    if (!val) return "Brak daty";
    const d = new Date(val);
    if (isNaN(d)) return String(val);
    return d.toLocaleDateString("pl-PL");
  };

  useEffect(() => {
    if (!zakupyData.length || !selectedYear) {
      setRows([]);
      return;
    }

    const filtered = zakupyData.filter((row) => {
      const y = parseInt(row.Rok, 10);
      const m = parseInt(row.Miesiac, 10);
      const yearMatch = String(y) === String(selectedYear);
      const monthMatch =
        selectedMonth === "all" || m === parseInt(selectedMonth, 10);
      return yearMatch && monthMatch;
    });

    const negatives = filtered
      .map((row) => ({
        supplierName: row.NazwaDostawcy || "Nieznany dostawca",
        documentNo: row.DowodZakupu || "—",
        issueDate: row.DataZakupu || row.DataWystawienia || "Brak daty",
        value: calculateTransactionValue(row),
      }))
      .filter((row) => row.value < 0)
      .sort((a, b) => a.value - b.value) // most negative first
      .slice(0, 10);

    setRows(negatives);
  }, [zakupyData, selectedYear, selectedMonth]);

  return (
    <TableWrapper>
      <TitleWrapper>
        🛠 Top 10 Największe Ujemne Transakcje Zakupowe (Korekty)
        <InfoIconWrapper data-tooltip-id="top-neg-purchases-tooltip">
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>

      <InfoTooltip
        id="top-neg-purchases-tooltip"
        place="right"
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "#fff",
        }}
        content={
          <>
            <strong>Największe korekty zakupowe</strong>
            <br />
            Filtruj po roku i miesiącu. Lista prezentuje 10 najbardziej
            ujemnych pojedynczych transakcji zakupu (korekt), wraz z numerem
            dokumentu i datą.
            <br />
            Wartość liczona jako suma K_42 + K_43 (ujemne wartości).
          </>
        }
      />

      {/* Filters */}
      <FiltersWrapper>
        <div>
          <label style={{ marginRight: 6 }}>Rok:</label>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label style={{ marginRight: 6 }}>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="all">Cały rok</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {monthNames[m - 1]}
              </option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      <StyledTable>
        <thead>
          <tr>
            <th>Miejsce</th>
            <th>Dostawca</th>
            <th>Dowód zakupu</th>
            <th>Data zakupu</th>
            <th>Kwota (PLN)</th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((tx, index) => (
              <tr
                key={`${tx.documentNo}-${index}`}
                className={index < 3 ? "highlight-low" : ""}
              >
                <td>
                  {index + 1 === 1
                    ? "🥇"
                    : index + 1 === 2
                    ? "🥈"
                    : index + 1 === 3
                    ? "🥉"
                    : index + 1}
                </td>
                <td>{tx.supplierName}</td>
                <td>{tx.documentNo}</td>
                <td>{fmtDate(tx.issueDate)}</td>
                <td>
                  {tx.value.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5">
                Brak dostępnych danych dla wybranych filtrów.
              </td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
};

export default Top10NegativePurchaseTransactions;
